import { useAuth } from "@/core/auth/hooks/useAuth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCurrentBusiness } from "../../business/hooks/useCurrentBusiness";
import { appointmentApiService } from "../services/appointmentApiService";
import { fromAppointmentEventDtoToAppointmentEvent } from "../types/appointmentEventTypes";
import {
  fromAppointmentDtoToAppointment,
  fromCreateAppointmentRequestToCreateAppointmentDto,
  fromUpdateAppointmentRequestToUpdateAppointmentDto,
  type Appointment,
  type CreateAppointmentRequest,
  type UpdateAppointmentRequest,
} from "../types/appointmentTypes";

export const appointmentKeys = {
  all: ["appointment"] as const,
  list: (businessId?: string, userId?: string) =>
    businessId ? [...appointmentKeys.all, "list", businessId, userId] as const : [...appointmentKeys.all, "list"] as const,
  detail: (id: string, businessId?: string, userId?: string) =>
    businessId ? [...appointmentKeys.all, "detail", id, businessId, userId] as const : [...appointmentKeys.all, "detail", id] as const,
  events: (id: string, businessId?: string, userId?: string) =>
    [...appointmentKeys.all, "events", id, businessId, userId] as const,
};

export function useAppointmentEvents(id: string) {
  const { business, businessId } = useCurrentBusiness();
  const { user } = useAuth();
  return useQuery({
    queryKey: appointmentKeys.events(id, businessId, user?.id),
    queryFn: async ({ signal }) => {
      if (!businessId) throw new Error("Choose a business first.");
      const dtos = await appointmentApiService.getEvents(businessId, id, signal);
      signal.throwIfAborted();
      const events = dtos.map(fromAppointmentEventDtoToAppointmentEvent);
      if (events.some((event) => event.appointmentId !== id)) throw new Error("History not found for this appointment.");
      return events;
    },
    enabled: !!id && !!businessId && !!user && business?.ownerId === user.id,
    staleTime: 60_000,
  });
}

export const useAppointments = () => {
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();
  return useQuery<Appointment[], Error>({
    queryKey: appointmentKeys.list(businessId, user?.id),
    queryFn: async ({ signal }) => {
      if (!businessId) throw new Error("Choose a business first.");
      const dtos = await appointmentApiService.getAll(businessId);
      signal.throwIfAborted();
      return dtos.map(fromAppointmentDtoToAppointment).filter((item) => item.businessId === businessId);
    },
    enabled: !!businessId && !!user,
    staleTime: 60_000,
  });
};

export const useAppointment = (id: string) => {
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();
  return useQuery<Appointment, Error>({
    queryKey: appointmentKeys.detail(id, businessId, user?.id),
    queryFn: async ({ signal }) => {
      const appointment = fromAppointmentDtoToAppointment(await appointmentApiService.getById(id));
      signal.throwIfAborted();
      if (appointment.businessId !== businessId) throw new Error("Appointment not found in this business.");
      return appointment;
    },
    enabled: !!id && !!businessId && !!user,
    staleTime: 60_000,
  });
};

interface AppointmentMutationScope {
  businessId: string;
  userId: string;
  generation: number;
}

function useAppointmentCache() {
  const queryClient = useQueryClient();
  const { businessId } = useCurrentBusiness();
  const { user, getAuthGeneration } = useAuth();
  return {
    onMutate: (): AppointmentMutationScope => {
      if (!businessId || !user) throw new Error("Choose a business and sign in before changing appointments.");
      return { businessId, userId: user.id, generation: getAuthGeneration() };
    },
    onSuccess: async (appointment: Appointment, _variables: unknown, scope: AppointmentMutationScope | undefined) => {
      if (!scope || scope.generation !== getAuthGeneration() || appointment.businessId !== scope.businessId) return;
      queryClient.setQueryData(appointmentKeys.detail(appointment.id, scope.businessId, scope.userId), appointment);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: appointmentKeys.list(scope.businessId, scope.userId) }),
        queryClient.invalidateQueries({ queryKey: appointmentKeys.events(appointment.id, scope.businessId, scope.userId) }),
        queryClient.invalidateQueries({ queryKey: ["availableTimeSlots", { businessId: scope.businessId, userId: scope.userId }] }),
        queryClient.invalidateQueries({ queryKey: ["search"] }),
      ]);
    },
  };
}

export const useCreateAppointment = () => {
  const { businessId } = useCurrentBusiness();
  const cache = useAppointmentCache();
  return useMutation<Appointment, Error, CreateAppointmentRequest, AppointmentMutationScope>({
    meta: { action: "appointment.create" },
    mutationFn: async (data) => {
      if (!businessId || data.businessId !== businessId) throw new Error("The selected business changed. Reopen this form.");
      return fromAppointmentDtoToAppointment(await appointmentApiService.create(fromCreateAppointmentRequestToCreateAppointmentDto(data)));
    },
    ...cache,
  });
};

export const useUpdateAppointment = (id: string) => {
  const { businessId } = useCurrentBusiness();
  const cache = useAppointmentCache();
  return useMutation<Appointment, Error, UpdateAppointmentRequest, AppointmentMutationScope>({
    meta: { action: "appointment.update" },
    mutationFn: async (data) => {
      const current = fromAppointmentDtoToAppointment(await appointmentApiService.getById(id));
      if (current.businessId !== businessId) throw new Error("Appointment not found in this business.");
      if (!["pending", "confirmed"].includes(current.status)) throw new Error("This appointment can no longer be edited.");
      const dto = fromUpdateAppointmentRequestToUpdateAppointmentDto(data);
      return fromAppointmentDtoToAppointment(await appointmentApiService.update(id, dto));
    },
    ...cache,
  });
};

function useAppointmentStatusMutation(id: string, action: "confirm" | "cancel") {
  const { businessId } = useCurrentBusiness();
  const cache = useAppointmentCache();
  return useMutation<Appointment, Error, void, AppointmentMutationScope>({
    meta: { action: `appointment.${action}` as const },
    mutationFn: async () => {
      const current = fromAppointmentDtoToAppointment(await appointmentApiService.getById(id));
      if (current.businessId !== businessId) throw new Error("Appointment not found in this business.");
      if (action === "confirm" ? current.status !== "pending" : !["pending", "confirmed"].includes(current.status)) {
        throw new Error("This appointment's status changed. Refresh before continuing.");
      }
      return fromAppointmentDtoToAppointment(await appointmentApiService[action](id));
    },
    ...cache,
  });
}

export const useConfirmAppointment = (id: string) => useAppointmentStatusMutation(id, "confirm");
export const useCancelAppointment = (id: string) => useAppointmentStatusMutation(id, "cancel");
